/**
 * company-profile.ts
 * The tenant's own company profile (GET/PATCH /admin/identity/company): the
 * platform-tier company detail plus the fiscal data its documents print as
 * the issuer (purchase orders) — domicilio and condición frente al IVA.
 *
 * Kept apart from platform-company.ts so the platform-tier routes keep their
 * own shape untouched.
 */
import { z } from 'zod';
import { PatchCompanyFieldsBodySchema, PlatformCompanyDetailSchema } from './platform-company.js';
import { TaxConditionSchema } from './fiscal.js';

/**
 * Days before an open approval task is reminded (then again every N days) and
 * before it also reaches the company admin (odd/tasks/notification-tasks.md, S7).
 */
export const TaskReminderDaysSchema = z.number().int().min(1).max(60);

// sign_in_method is platform-only: a tenant admin can neither see nor change it here.
export const CompanyProfileSchema = PlatformCompanyDetailSchema.omit({ sign_in_method: true }).extend({
  address: z.string().nullable(),
  tax_condition: TaxConditionSchema.nullable(),
  task_reminder_days: z.number().int(),
  task_escalation_days: z.number().int(),
});
export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

/** `null` clears a fiscal field. */
export const PatchCompanyProfileBodySchema = PatchCompanyFieldsBodySchema.omit({ sign_in_method: true }).extend({
  address: z.string().trim().max(300).nullable().optional(),
  taxCondition: TaxConditionSchema.nullable().optional(),
  taskReminderDays: TaskReminderDaysSchema.optional(),
  taskEscalationDays: TaskReminderDaysSchema.optional(),
});
export type PatchCompanyProfileBody = z.infer<typeof PatchCompanyProfileBodySchema>;
